/*
 * Cart drawer / cart page upsell item.
 *
 * Adds via the shared surface-aware window.arCartMutate() (POSTs /cart/add
 * asking for the cart section, refreshes in place -- drawer OR page). Errors
 * (sold out) are shown inline. Variant dropdowns update the chosen variant.
 */
(() => {
  if (customElements.get('cart-drawer-upsell')) {
    return;
  }

  class CartDrawerUpsell extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        variantSelect: '[data-select]',
        variantsData: '[type="application/json"]',
        variantInput: '[name="id"]',
        priceElement: '[data-price]'
      };

      this.form = null;
      this.submitButton = null;
      this.variants = null;
      this.currentVariant = null;
      this.options = null;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.form.elements.add;

      const variantsEl = this.querySelector(this.selectors.variantsData);
      try {
        this.variants = JSON.parse((variantsEl && variantsEl.textContent) || '[]');
      } catch (e) {
        this.variants = [];
      }

      this.updateOptions();
      this.updateVariant();
      this.setHandlers();
      this.updateVariantInputId();
    }

    get addUrl() {
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (root.charAt(root.length - 1) === '/') root = root.slice(0, -1);
      return root + '/cart/add';
    }

    selectedId() {
      if (this.currentVariant && this.currentVariant.id) return this.currentVariant.id;
      const input = this.form && this.form.querySelector(this.selectors.variantInput);
      return input ? input.value : null;
    }

    updateVariantInputId() {
      if (!this.currentVariant) return;
      const input = this.form.querySelector(this.selectors.variantInput);
      if (input) input.value = this.currentVariant.id;
    }

    updateOptions() {
      this.options = Array.from(
        this.querySelectorAll(this.selectors.variantSelect),
        select => ({ name: select.dataset.name, value: select.value })
      );
    }

    updateVariant() {
      if (this.options.length) {
        this.currentVariant = this.variants.find(variant =>
          !variant.options
            .map((option, index) => this.options[index].value === option)
            .includes(false)
        );
      } else {
        this.currentVariant =
          this.variants.find(variant => variant.available) || this.variants[0];
      }
    }

    setHandlers() {
      this.form.addEventListener('submit', event => this.onSubmit(event));

      // Also bind the button directly so it works regardless of form quirks.
      if (this.submitButton) {
        this.submitButton.addEventListener('click', event => {
          event.preventDefault();
          this.onSubmit(event);
        });
      }

      this.querySelectorAll(this.selectors.variantSelect).forEach(select => {
        select.addEventListener('change', this.onVariantChange.bind(this));
      });
    }

    onSubmit(event) {
      if (event) event.preventDefault();
      this.addToCart();
    }

    addToCart() {
      const id = this.selectedId();
      if (!id) return;
      if (this.submitButton && this.submitButton.disabled) return;

      this.setBusy(true);
      var self = this;

      if (typeof window.arCartMutate === 'function') {
        window.arCartMutate(
          this.addUrl,
          { id: id, quantity: 1 },
          function (errData) {
            self.setBusy(false);
            self.showError((errData && (errData.description || errData.message)) || 'Could not add this item.');
          }
        ).then(function () {
          self.setBusy(false);
        });
        return;
      }

      // Fallback: direct add then open the cart page.
      fetch(this.addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 })
      })
        .then(response => response.json().then(data => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          this.setBusy(false);
          if (!ok) {
            this.showError((data && (data.description || data.message)) || 'Could not add this item.');
            return;
          }
          window.arGoToCart();
        })
        .catch(error => {
          this.setBusy(false);
          console.error('Upsell add to cart failed', error);
          this.showError('Could not add this item. Please try again.');
        });
    }

    setBusy(isBusy) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isBusy;
      this.submitButton.classList.toggle('is-loading', isBusy);
    }

    showError(message) {
      let el = this.querySelector('[data-upsell-error]');
      if (!el) {
        el = document.createElement('p');
        el.setAttribute('data-upsell-error', '');
        el.style.cssText =
          'flex:1 1 100%;margin:.4rem 0 0;color:#b3325a;font-size:1.2rem;';
        this.appendChild(el);
      }
      el.textContent = message;
    }

    onVariantChange() {
      this.updateOptions();
      this.updateVariant();
      this.toggleAddButton(false, '');

      if (!this.currentVariant) {
        this.toggleAddButton(true, '');
        this.setUnavailable();
        return;
      }

      if (!this.currentVariant.available) {
        this.toggleAddButton(true, (window.variantStrings || {}).soldOut || 'Sold out');
      }

      this.updateVariantInputId();
      this.updateProductInfo();
    }

    setUnavailable() {
      if (!this.submitButton) return;
      const span = this.submitButton.querySelector('span');
      if (span) {
        span.textContent = (window.variantStrings || {}).unavailable || 'Unavailable';
      }
    }

    toggleAddButton(disable, text) {
      if (!this.submitButton) return;
      const span = this.submitButton.querySelector('span');

      if (disable) {
        this.submitButton.setAttribute('disabled', 'disabled');
        if (text && span) span.textContent = text;
        return;
      }

      this.submitButton.removeAttribute('disabled');
      if (span) {
        span.textContent = (window.variantStrings || {}).addToCartShort || 'Add';
      }
    }

    updateProductInfo() {
      if (!this.dataset.url || !this.currentVariant) return;

      fetch(`${this.dataset.url}&variant=${this.currentVariant.id}`)
        .then(response => response.text())
        .then(responseText => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const source = html.querySelector(this.selectors.priceElement);
          const destination = this.querySelector(this.selectors.priceElement);
          if (!source || !destination) return;
          destination.innerHTML = source.innerHTML;
        })
        .catch(error => console.error('Upsell price refresh failed', error));
    }
  }

  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
})();